import React, { FC } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import { StepResult } from '../model';

type NonSuccessOutcome = 'failed' | 'skipped';

const NON_SUCCESS_STEP_DISPLAY: Record<
  NonSuccessOutcome,
  { label: string; chipColor: 'error' | 'default'; captionColor: 'error' | 'text.secondary' }
> = {
  failed: { label: 'Failed', chipColor: 'error', captionColor: 'error' },
  skipped: { label: 'Skipped', chipColor: 'default', captionColor: 'text.secondary' },
};

function getStepOutcome(
  value: string | StepResult,
): { outcome: 'success' } | { outcome: NonSuccessOutcome; detail?: string } {
  if (typeof value === 'object' && value.status === 'failed') return { outcome: 'failed', detail: value.error };
  if (typeof value === 'object' && value.status === 'skipped') return { outcome: 'skipped', detail: value.reason };
  return { outcome: 'success' };
}

const StepResultsList: FC<{ steps: Record<string, string | StepResult> }> = ({ steps }) => {
  const entries = Object.entries(steps);

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No steps recorded yet.
      </Typography>
    );
  }

  return (
    <>
      {entries.map(([stepName, value]) => {
        const row = getStepOutcome(value);
        return (
          <Box
            key={stepName}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 1, borderBottom: '1px solid #eee' }}
          >
            <Typography variant="body2">{stepName}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {row.outcome === 'success' ? (
                <Chip label="Success" color="success" size="small" icon={<CheckCircleIcon />} />
              ) : (
                <>
                  <Chip
                    label={NON_SUCCESS_STEP_DISPLAY[row.outcome].label}
                    color={NON_SUCCESS_STEP_DISPLAY[row.outcome].chipColor}
                    size="small"
                    {...(row.outcome === 'failed' ? { icon: <ErrorIcon /> } : {})}
                  />
                  {row.detail && (
                    <Typography variant="caption" color={NON_SUCCESS_STEP_DISPLAY[row.outcome].captionColor}>
                      {row.detail}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default StepResultsList;
